// src/pages/ProjectsPage.tsx - Refactored with filter search and direct project creation
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Assessment as ProjectIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as PlayIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getProjectsList, createProject, deleteProject } from '../utils/projectApi';

interface Project {
  project_id: string;
  user_id: string;
  title: string;
  description?: string;
  status: 'draft' | 'completed';
  created_at: string;
  updated_at: string;
  progress: {
    info: boolean;
    analysis: boolean;
    data: boolean;
    report: boolean;
  };
  progress_percentage: number;
  analysis_type?: string;
  file_count?: number;
  data_size_bytes?: number;
  notes: Array<any>;
  tags: string[];
  timeline: Array<any>;
}

interface ToastMessage {
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const ProjectsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Filter state
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuProject, setMenuProject] = useState<Project | null>(null);

  const { currentUser, refreshUserProfile } = useAuth();
  const navigate = useNavigate();

  // Fetch projects from API
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!currentUser) {
        setError('You must be logged in to view projects');
        setProjects([]);
        setFilteredProjects([]);
        return;
      }

      const data = await getProjectsList();
      const fetchedProjects = data.projects || [];
      
      console.log(`Fetched ${fetchedProjects.length} projects:`, fetchedProjects);
      setProjects(fetchedProjects);
      setFilteredProjects(fetchedProjects);
      
      // Extract all unique tags for the filter
      const tags = new Set<string>();
      fetchedProjects.forEach((project: Project) => {
        project.tags?.forEach((tag: string) => tags.add(tag));
      });
      setAllTags(Array.from(tags).sort());
      
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to load projects');
      setProjects([]);
      setFilteredProjects([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Load projects when component mounts
  useEffect(() => {
    if (currentUser) {
      console.log('User authenticated, fetching projects...');
      fetchProjects();
    } else {
      console.log('No user authenticated, clearing projects');
      setProjects([]);
      setFilteredProjects([]);
      setLoading(false);
      setError(null);
    }
  }, [currentUser, fetchProjects]);

  // Filter projects based on selected tags
  useEffect(() => {
    if (selectedTags.length === 0) {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter(project =>
        selectedTags.some(tag => project.tags?.includes(tag))
      );
      setFilteredProjects(filtered);
    }
  }, [projects, selectedTags]);

  const handleCreateProject = async () => {
    try {
      setCreating(true);
      
      // Create a minimal project with default title
      const data = await createProject({
        title: 'Untitled Project',
        description: ''
      });

      showToast('Project created! Add a title and description below.', 'success');
      
      // Navigate directly to the project detail page
      navigate(`/dashboard/projects/${data.project.project_id}`);
      
      // Refresh user profile to update project count in sidebar
      if (refreshUserProfile) {
        refreshUserProfile();
      }
      
    } catch (err) {
      console.error('Error creating project:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create project';
      showToast(errorMessage, 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = (project: Project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleOpenProject = (project: Project) => {
    navigate(`/dashboard/projects/${project.project_id}`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, project: Project) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuProject(project);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuProject(null);
  };

  const showToast = (message: string, severity: ToastMessage['severity']) => {
    setToast({ message, severity });
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      setDeleting(true);
      await deleteProject(projectToDelete.project_id);

      setProjects(prev => prev.filter(p => p.project_id !== projectToDelete.project_id));
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
      showToast('Project deleted successfully!', 'success');
      
      // Refresh user profile to update project count in sidebar
      if (refreshUserProfile) {
        refreshUserProfile();
      }
      
    } catch (err) {
      console.error('Error deleting project:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete project';
      showToast(errorMessage, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const getProgressColor = (percentage: number) => {
    if (percentage === 100) return 'success';
    if (percentage >= 50) return 'warning';
    return 'error';
  };

  const getStatusChip = (project: Project) => {
    if (project.progress_percentage === 100) {
      return <Chip label="Complete" size="small" color="success" />;
    } else {
      return <Chip label={`${Math.round(project.progress_percentage)}% Complete`} size="small" color="warning" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Loading your projects...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert 
          severity="error" 
          action={
            <Box>
              <Button color="inherit" size="small" onClick={fetchProjects} sx={{ mr: 1 }}>
                Retry
              </Button>
              <Button color="inherit" size="small" onClick={() => setError(null)}>
                Dismiss
              </Button>
            </Box>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // Main render with projects or empty state
  return (
    <>
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            My Projects
          </Typography>
          {projects.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {filteredProjects.length} of {projects.length} project{projects.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        {/* Filter Bar - Only show if there are projects and tags */}
        {projects.length > 0 && allTags.length > 0 && (
          <Box mb={4}>
            <Autocomplete
              multiple
              options={allTags}
              value={selectedTags}
              onChange={(event, newValue) => setSelectedTags(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Filter by tags..."
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ maxWidth: 500 }}
            />
          </Box>
        )}

        {/* Show empty state if no projects */}
        {projects.length === 0 ? (
          <Box 
            textAlign="center" 
            py={8}
            sx={{ 
              bgcolor: 'grey.50', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <ProjectIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No projects yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Create your first project to start analyzing ecological data
            </Typography>
            <Button 
              variant="contained" 
              startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />} 
              onClick={handleCreateProject}
              size="large"
              disabled={creating}
            >
              {creating ? 'Creating...' : 'Create Your First Project'}
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Create New Project Card */}
            <Grid item xs={12} sm={6} md={4}>
              <Card
                sx={{
                  height: 280,
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(0, 200, 83, 0.05)',
                  cursor: creating ? 'default' : 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: creating ? 0.7 : 1,
                  '&:hover': creating ? {} : {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 32px rgba(0, 200, 83, 0.2)',
                    bgcolor: 'rgba(0, 200, 83, 0.1)',
                  }
                }}
                onClick={creating ? undefined : handleCreateProject}
              >
                  {creating ? (
                    <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
                  ) : (
                    <AddIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                  )}
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {creating ? 'Creating Project...' : 'Create New Project'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {creating ? 'Please wait...' : 'Start analyzing your data'}
                  </Typography>
                </Card>
            </Grid>

            {/* Project Cards */}
            {filteredProjects.map((project, index) => (
              <Grid item xs={12} sm={6} md={4} key={project.project_id}>
                <Card
                    sx={{
                      height: 280,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      }
                    }}
                    onClick={() => handleOpenProject(project)}
                  >
                    {/* Menu Button */}
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1,
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.9)',
                        }
                      }}
                      onClick={(e) => handleMenuOpen(e, project)}
                    >
                      <MoreVertIcon />
                    </IconButton>

                    <CardContent sx={{ height: '75%', display: 'flex', flexDirection: 'column', pt: 3 }}>
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                        <Box display="flex" alignItems="flex-start" flex={1}>
                          <ProjectIcon sx={{ color: 'primary.main', mr: 1, mt: 0.5 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, pr: 1 }}>
                            {project.title}
                          </Typography>
                        </Box>
                        {getStatusChip(project)}
                      </Box>

                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {project.description || 'No description provided'}
                      </Typography>

                      <Box mb={2}>
                        <Typography variant="caption" color="text.secondary">
                          Progress: {Math.round(project.progress_percentage)}%
                        </Typography>
                        <Box
                          sx={{
                            width: '100%',
                            height: 6,
                            bgcolor: 'grey.200',
                            borderRadius: 1,
                            mt: 0.5,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${project.progress_percentage}%`,
                              height: '100%',
                              bgcolor: `${getProgressColor(project.progress_percentage)}.main`,
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </Box>
                      </Box>

                      <Box flex={1}>
                        {project.tags && project.tags.length > 0 && (
                          <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                            {project.tags.slice(0, 3).map((tag, idx) => (
                              <Chip 
                                key={idx} 
                                label={tag} 
                                size="small" 
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            ))}
                            {project.tags.length > 3 && (
                              <Chip 
                                label={`+${project.tags.length - 3}`} 
                                size="small" 
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                          </Box>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Updated {new Date(project.updated_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                      <Button
                        size="small"
                        startIcon={<PlayIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenProject(project);
                        }}
                      >
                        Continue
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        {project.notes?.length || 0} notes
                      </Typography>
                    </CardActions>
                  </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Context Menu - Only showing delete now */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
      >
        <MenuItem onClick={() => menuProject && handleDeleteProject(menuProject)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete Project</ListItemText>
        </MenuItem>
      </Menu>

      {/* Toast Notifications */}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast(null)}
          severity={toast?.severity}
          sx={{ width: '100%' }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{projectToDelete?.title}"? 
            This will delete all project data, notes, tags, and timeline events. This action cannot be undone and may take a few minutes depending on the size of your project. 
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteProject}
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete Project'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectsPage;